import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Pagination } from '@/components/ui/pagination';
import { useClientPagination } from '@/hooks/useClientPagination';
import { PageLoading } from '@/components/ui/loading';
import { formatDateTime } from '@/lib/utils';

export default function AuditLogsPage() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', action, entityType],
    queryFn: async () => { const r = await api.get<{ data: any[] }>(`/audit-logs?action=${action}&entityType=${entityType}&limit=1000`); return r.data ?? []; },
  });
  const { page, setPage, pageSize, total, pageItems } = useClientPagination(data ?? [], {
    resetKey: `${action}|${entityType}`,
  });
  if (isLoading) return <PageLoading />;
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold">Audit Logs</h1>
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Filter by action..." value={action} onChange={e => setAction(e.target.value)} className="w-full" />
            <Input placeholder="Filter by entity type..." value={entityType} onChange={e => setEntityType(e.target.value)} className="w-full" />
          </div>
        </CardContent>
      </Card>
      <Card><CardContent className="p-0">
        <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow><TableIndexHead /><TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Details</TableHead></TableRow></TableHeader>
            <TableBody>
              {pageItems.map((l: any, index: number) => (
                <TableRow key={l.id}>
                  <TableIndexCell value={rowSerial(index, page, pageSize)} />
                  <TableCell className="text-sm">{formatDateTime(l.created_at)}</TableCell>
                  <TableCell>{l.user_name || 'System'}</TableCell>
                  <TableCell><Badge variant="info">{l.action}</Badge></TableCell>
                  <TableCell className="text-sm">{l.entity_type} {l.entity_id ? `(${l.entity_id.slice(0, 8)}...)` : ''}</TableCell>
                  <TableCell className="text-sm text-secondary max-w-xs truncate">{l.details || '-'}</TableCell>
                </TableRow>
              ))}
              {(!data || data.length === 0) && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted">No logs</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="md:hidden space-y-3 p-4">
          {!data || data.length === 0 ? (
            <p className="py-8 text-center text-muted">No logs</p>
          ) : pageItems.map((l: any) => (
            <div key={l.id} className="rounded-lg border border-subtle bg-surface p-4 space-y-2 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-secondary">{formatDateTime(l.created_at)}</p>
                <Badge variant="info">{l.action}</Badge>
              </div>
              <p className="font-medium text-primary break-words">{l.user_name || 'System'}</p>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between gap-3">
                  <dt className="text-secondary shrink-0">Entity</dt>
                  <dd className="text-primary text-right break-words">{l.entity_type} {l.entity_id ? `(${l.entity_id.slice(0, 8)}...)` : ''}</dd>
                </div>
                {l.details && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-secondary shrink-0">Details</dt>
                    <dd className="text-primary text-right break-words">{l.details}</dd>
                  </div>
                )}
              </dl>
            </div>
          ))}
        </div>
        {(data?.length ?? 0) > 0 && <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />}
      </CardContent></Card>
    </div>
  );
}
